import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    ContentCopyOutlined,
    GroupAddOutlined,
    OpenInNewOutlined,
    RefreshOutlined,
    SearchOutlined,
    SyncOutlined,
    TaskAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Snackbar,
    TextField,
    Tooltip,
} from "@mui/material";

import { useGetInProgressOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";

import "./EcrOpsNewOrgs.css";

// ── Copy helper ───────────────────────────────────────────────────────────────

function useCopyToast() {
    const [open, setOpen] = useState(false);
    const copy = (text) => {
        navigator.clipboard.writeText(text).then(() => setOpen(true));
    };
    return { open, setOpen, copy };
}

// ── Org row ───────────────────────────────────────────────────────────────────

const OrgRow = ({ org, onCopy, onComplete }) => (
    <div className="ecr-new-orgs__row">
        {/* Name + GitHub link */}
        <div className="ecr-new-orgs__col ecr-new-orgs__col--name">
            <span className="ecr-new-orgs__name">{org.name}</span>
            {org.additional_info?.github_org_link && (
                <Tooltip title="Open GitHub org" arrow>
                    <a
                        href={org.additional_info.github_org_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ecr-new-orgs__gh-link"
                    >
                        <OpenInNewOutlined sx={{ fontSize: 14 }} />
                    </a>
                </Tooltip>
            )}
        </div>

        {/* Ref ID */}
        <div className="ecr-new-orgs__col ecr-new-orgs__col--ref">
            <span className="ecr-new-orgs__label-tiny">Ref ID</span>
            <div className="ecr-new-orgs__copy-row">
                <code className="ecr-new-orgs__code">{org.ref_id}</code>
                <Tooltip title="Copy Ref ID" arrow>
                    <IconButton
                        size="small"
                        onClick={() => onCopy(String(org.ref_id))}
                        sx={{ padding: "2px" }}
                    >
                        <ContentCopyOutlined sx={{ fontSize: 13, color: "#9ca3af" }} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>

        {/* Org ID */}
        <div className="ecr-new-orgs__col ecr-new-orgs__col--id">
            <span className="ecr-new-orgs__label-tiny">Org ID</span>
            <div className="ecr-new-orgs__copy-row">
                <code className="ecr-new-orgs__code ecr-new-orgs__code--id">{org._id}</code>
                <Tooltip title="Copy Org ID" arrow>
                    <IconButton
                        size="small"
                        onClick={() => onCopy(org._id)}
                        sx={{ padding: "2px" }}
                    >
                        <ContentCopyOutlined sx={{ fontSize: 13, color: "#9ca3af" }} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>

        {/* Status badge */}
        <div className="ecr-new-orgs__col ecr-new-orgs__col--status">
            <Chip
                label="In-Progress"
                size="small"
                sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    background: "#fef3c7",
                    color: "#92400e",
                    border: "1px solid #fcd34d",
                }}
            />
        </div>

        {/* Action */}
        <div className="ecr-new-orgs__col ecr-new-orgs__col--action">
            <Button
                variant="contained"
                size="small"
                startIcon={<TaskAltOutlined sx={{ fontSize: 14 }} />}
                onClick={() => onComplete(org)}
                sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    background: "#86BC25",
                    whiteSpace: "nowrap",
                    padding: "5px 12px",
                    "&:hover": { background: "#6fa019" },
                }}
            >
                Complete Creation
            </Button>
        </div>
    </div>
);

OrgRow.propTypes = {
    org: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        name: PropTypes.string,
        ref_id: PropTypes.number,
        additional_info: PropTypes.shape({
            github_org_link: PropTypes.string,
        }),
    }).isRequired,
    onCopy: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

const SkeletonRow = () => (
    <div className="ecr-new-orgs__row ecr-new-orgs__row--skeleton">
        <div className="ecr-new-orgs__skeleton-bar" style={{ width: "40%" }} />
        <div className="ecr-new-orgs__skeleton-bar" style={{ width: "15%" }} />
        <div className="ecr-new-orgs__skeleton-bar" style={{ width: "25%" }} />
        <div className="ecr-new-orgs__skeleton-bar" style={{ width: "10%" }} />
    </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const EcrOpsNewOrgs = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const { open: toastOpen, setOpen: setToastOpen, copy } = useCopyToast();

    const handleComplete = (org) => {
        navigate(`/ecr-ops/complete-org/${org._id}`, {
            state: { orgName: org.name },
        });
    };

    // Falls back to the default (first) platform until the ECR Ops platform
    // selector (EcrOpsLayout) has resolved a selection.
    const { data: platformData } = useGetPlatformsQuery();
    const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
    const platformId = selectedPlatformId || platformData?._id;

    const {
        data: orgs,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useGetInProgressOrganizationsQuery(
        { platformId },
        { skip: !platformId, refetchOnMountOrArgChange: true }
    );

    const filtered = useMemo(() => {
        const list = orgs || [];
        if (!search.trim()) return list;
        const q = search.toLowerCase();
        return list.filter(
            (o) =>
                o.name?.toLowerCase().includes(q) ||
                String(o.ref_id).includes(q) ||
                o._id?.toLowerCase().includes(q)
        );
    }, [orgs, search]);

    const renderTableBody = () => {
        if (isLoading) {
            // eslint-disable-next-line react/no-array-index-key
            return Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />);
        }
        if (isError) {
            return (
                <div className="ecr-new-orgs__empty">
                    <Alert severity="error" sx={{ fontSize: "13px" }}>
                        Failed to load In-Progress organisations. Please try again.
                    </Alert>
                </div>
            );
        }
        if (filtered.length === 0) {
            const msg = search
                ? "No organisations match your search."
                : "No In-Progress organisations found.";
            return <div className="ecr-new-orgs__empty"><p>{msg}</p></div>;
        }
        return filtered.map((org) => (
            <OrgRow key={org._id} org={org} onCopy={copy} onComplete={handleComplete} />
        ));
    };

    return (
        <div>
            {/* Page header */}
            <div className="ecr-ops-page-header">
                <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal</p>
                <h1 className="ecr-ops-page-header__title">New Organisations</h1>
                <p className="ecr-ops-page-header__sub">
                    Organisations currently in <strong style={{ color: "#fbbf24" }}>In-Progress</strong> status —
                    provisioning has been triggered but is not yet complete.
                </p>
            </div>

            <div className="ecr-ops-content ecr-new-orgs">
                {/* Toolbar */}
                <div className="ecr-new-orgs__toolbar">
                    <TextField
                        size="small"
                        placeholder="Search by name, ref ID or org ID…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined sx={{ fontSize: 18, color: "#9ca3af" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: 340,
                            "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                                background: "#fff",
                                borderRadius: "8px",
                            },
                        }}
                    />

                    <span className="ecr-new-orgs__count">
                        {(() => {
                            if (isLoading || isFetching) return "—";
                            const n = filtered.length;
                            return `${n} ${n === 1 ? "org" : "orgs"}`;
                        })()}
                    </span>

                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                size="small"
                                onClick={refetch}
                                disabled={isFetching}
                                sx={{ color: "#6b7280" }}
                            >
                                {isFetching ? (
                                    <CircularProgress size={16} sx={{ color: "#86BC25" }} />
                                ) : (
                                    <RefreshOutlined fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>

                {/* Quick Actions */}
                <div className="ecr-new-orgs__quick-actions">
                    <div className="ecr-new-orgs__quick-actions-header">
                        <span className="ecr-new-orgs__quick-actions-title">Quick Actions</span>
                        <span className="ecr-new-orgs__quick-actions-desc">
                            Helpful links for completing org setup
                        </span>
                    </div>
                    <div className="ecr-new-orgs__quick-actions-chips">
                        <a
                            href="https://appregv2.iam.deloitteresources.com/SpnUsersAndGroups/1f6d2937-240c-41ce-a27f-ad60e332d724"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ecr-new-orgs__action-chip"
                        >
                            <SyncOutlined className="ecr-new-orgs__action-chip-icon" />
                            <span className="ecr-new-orgs__action-chip-text">
                                Sync SG Groups to Azure App
                            </span>
                            <OpenInNewOutlined className="ecr-new-orgs__action-chip-external" />
                        </a>
                        <a
                            href="https://groups.aps.deloitteresources.com/IdentityManagement/aspx/groups/MyDLs.aspx"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ecr-new-orgs__action-chip"
                        >
                            <GroupAddOutlined className="ecr-new-orgs__action-chip-icon" />
                            <span className="ecr-new-orgs__action-chip-text">
                                Add Service Owner & Owners to Distribution Lists
                            </span>
                            <OpenInNewOutlined className="ecr-new-orgs__action-chip-external" />
                        </a>
                        <a
                            href="https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupsManagementMenuBlade/~/AllGroups"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ecr-new-orgs__action-chip"
                        >
                            <SearchOutlined className="ecr-new-orgs__action-chip-icon" />
                            <span className="ecr-new-orgs__action-chip-text">
                                Search Security Groups in Azure
                            </span>
                            <OpenInNewOutlined className="ecr-new-orgs__action-chip-external" />
                        </a>
                    </div>
                </div>

                {/* Table */}
                <div className="ecr-new-orgs__table">
                    {/* Header */}
                    <div className="ecr-new-orgs__header">
                        <span className="ecr-new-orgs__col ecr-new-orgs__col--name">Organisation Name</span>
                        <span className="ecr-new-orgs__col ecr-new-orgs__col--ref">Ref ID</span>
                        <span className="ecr-new-orgs__col ecr-new-orgs__col--id">Org ID</span>
                        <span className="ecr-new-orgs__col ecr-new-orgs__col--status">Status</span>
                        <span className="ecr-new-orgs__col ecr-new-orgs__col--action">Action</span>
                    </div>

                    {/* Body */}
                    {renderTableBody()}
                </div>
            </div>

            {/* Copy toast */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={2000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                message="Copied to clipboard"
            />
        </div>
    );
};

export default EcrOpsNewOrgs;
